<template>
  <div>
    <wb-card class="information-card">
      <div class="information-card__top-row">
        <div class="grid information-card__top-row--time">
          <div class="information-card__top-row--time-top">
            <wb-skeleton v-skeleton="'heading-s,m'"></wb-skeleton>
            <wb-skeleton
              v-skeleton="'heading-s,xxs'"
              :class="statusClass"
            ></wb-skeleton>
          </div>
          <wb-skeleton
            v-skeleton="'copy,s'"
            class="wb-margin-top-xxs"
          ></wb-skeleton>
        </div>
        <div class="information-card__top-row--aside grid">
          <wb-skeleton v-skeleton="'heading-s,xs,copy,xxs'"></wb-skeleton>
        </div>
      </div>
      <div class="information-card__collapsible">
        <div class="information-card__middle-row">
          <div class="left-block">
            <div class="grid dealer-information wb-margin-top-s">
              <wb-skeleton v-skeleton="'copy-secondary,l,,s'"></wb-skeleton>
              <wb-skeleton
                v-skeleton="'copy-secondary,m'"
                class="wb-margin-top-xxs wb-margin-start-s"
              ></wb-skeleton>
              <wb-skeleton
                v-skeleton="'copy-secondary,s'"
                class="margin-top-2xxs"
              ></wb-skeleton>
              <wb-skeleton
                v-skeleton="'copy-secondary,l,,m'"
                class="margin-top-2xxs"
              ></wb-skeleton>
            </div>
            <div class="grid additional-info mobile-hidden">
              <wb-skeleton v-skeleton="'copy,m'"></wb-skeleton>
              <wb-skeleton
                v-skeleton="'copy,full'"
                class="wb-margin-top-xxs"
              ></wb-skeleton>
            </div>
          </div>
          <div class="grid right-block wb-padding-top-s">
            <div class="grid grid-center vehicle-information">
              <wb-skeleton v-skeleton="'image,l'"></wb-skeleton>
              <wb-skeleton
                v-skeleton="'heading-xs,m'"
                class="wb-margin-top-s"
              ></wb-skeleton>
              <div class="flex flex-center margin-top-2xxs">
                <wb-skeleton v-skeleton="'copy-secondary,s,,xxs'"></wb-skeleton>
                <wb-skeleton
                  v-skeleton="'copy-secondary,s,,xxs'"
                  class="wb-margin-start-xs"
                ></wb-skeleton>
                <wb-skeleton
                  v-skeleton="'copy-secondary,s,,m'"
                  :class="detailSecondlineClass"
                ></wb-skeleton>
              </div>
              <div :class="cardBottomClass">
                <div class="grid">
                  <wb-skeleton
                    v-skeleton="'copy-secondary,l,,xs'"
                  ></wb-skeleton>
                  <wb-skeleton
                    v-skeleton="'copy-secondary,l,,xs'"
                    class="wb-margin-top-xxs"
                  ></wb-skeleton>
                  <wb-skeleton
                    v-skeleton="'copy-secondary,m,,xxs'"
                    class="wb-margin-top-xxs"
                  ></wb-skeleton>
                </div>
                <div class="grid grid-end">
                  <wb-skeleton
                    v-skeleton="'copy-secondary,s,,xxs'"
                  ></wb-skeleton>
                  <wb-skeleton
                    v-skeleton="'copy-secondary,s,,xxs'"
                    class="wb-margin-top-xxs"
                  ></wb-skeleton>
                  <wb-skeleton
                    v-skeleton="'copy-secondary,xs,,3xs'"
                    class="wb-margin-top-xxs"
                  ></wb-skeleton>
                </div>
              </div>
            </div>
          </div>
          <div class="grid additional-info desktop-hidden">
            <wb-skeleton v-skeleton="'copy,m'"></wb-skeleton>
            <wb-skeleton
              v-skeleton="'copy,full'"
              class="wb-margin-top-xxs"
            ></wb-skeleton>
            <wb-skeleton
              v-skeleton="'copy,full'"
              class="wb-margin-top-xxs"
            ></wb-skeleton>
          </div>
        </div>
        <div class="information-card__bottom-row">
          <div
            class="flex information-card__bottom-row--buttons"
            :class="buttonsContainer"
          >
            <wb-skeleton
              v-skeleton="'button-m,m'"
              class="mobile-hidden"
            ></wb-skeleton>
            <wb-skeleton
              v-skeleton="'copy,xs'"
              class="desktop-hidden"
            ></wb-skeleton>
            <wb-skeleton
              v-skeleton="'button-m,m,,full'"
              :class="buttonClass"
            ></wb-skeleton>
          </div>
        </div>
      </div>
    </wb-card>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent } from 'vue';
import { useMediaQuery } from '../../composables/media-query.composable';

import { SkeletonDirective } from '../../directives/skeleton.directive';

export default defineComponent({
  name: 'otb-information-card-skeleton',
  directives: {
    skeleton: SkeletonDirective,
  },
  setup() {
    const { isMobile } = useMediaQuery();

    const buttonClass = computed((): string => {
      return !isMobile.value ? 'wb-margin-start-xs' : 'wb-margin-top-xs';
    });
    const buttonsContainer = computed((): string => {
      return !isMobile.value ? 'flex-start' : 'flex-center flex-margin-top-s';
    });
    const detailSecondlineClass = computed((): string => {
      return !isMobile.value
        ? 'wb-margin-start-xs'
        : 'wb-margin-top-xxs description-last-item';
    });
    const statusClass = computed((): string => {
      return !isMobile.value ? 'wb-margin-start-xs' : 'wb-margin-bottom-xs';
    });
    const cardBottomClass = computed((): string => {
      return !isMobile.value ? 'flex wb-margin-top-s' : 'flex wb-margin-top-xs';
    });

    return {
      isMobile,
      buttonClass,
      buttonsContainer,
      detailSecondlineClass,
      statusClass,
      cardBottomClass,
    };
  },
});
</script>
<style scoped lang="scss">
@import './information-card.scss';
@import '~workbench-core/dist/scss/globals';

.information-card__middle-row .left-block .additional-info {
  @include wb-spacing('margin-top', 's');
}

.dealer-information,
.vehicle-information {
  background-color: var(--wb-grey-95);
  @include wb-spacing('padding', 's xs');
}

.vehicle-information {
  grid-template-columns: auto;
}

.flex {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  &-start {
    justify-content: flex-start;
  }

  &-center {
    justify-content: center;
  }

  &-margin-top-s {
    @include wb-spacing('margin', 's 0 0 0');
  }
}

.grid {
  display: grid;

  &-center {
    justify-items: center;
  }

  &-end {
    justify-items: end;
  }
}

.mobile-hidden {
  @include breakpoint-to(mq2) {
    display: none;
  }
}

.desktop-hidden {
  @include breakpoint-from(mq3) {
    display: none;
  }
}

.margin-top-2xxs {
  margin-top: calc(2 * var(--wb-spacing-xxs));
}

.description-last-item {
  @include wb-spacing('margin', 'xxs s 0 s');
}
</style>
